import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import { 
  Truck, 
  MapPin, 
  Phone, 
  Mail, 
  Shield, 
  Clock, 
  Cpu, 
  Target,
  ChevronDown,
  Menu,
  X,
  Send,
  Package,
  Warehouse,
  FileCheck,
  Loader2,
  ArrowRight
} from "lucide-react";
import { Toaster, toast } from "sonner";
import "@/App.css";

// Assets
const LOGO = "https://customer-assets.emergentagent.com/job_modern-ventures/artifacts/4ydxtyey_Globex%20Ventures%20PNG.png";
const HERO_BG = "https://images.unsplash.com/photo-1769144256227-5185141c3aca?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920";
const SERVICES_IMG = "https://customer-assets.emergentagent.com/job_modern-ventures/artifacts/6t74cwpw_Imagen1.jpg";
const ABOUT_IMG = "https://images.unsplash.com/photo-1742203102929-f2810971a534?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800";

// EmailJS Config
const EMAILJS_SERVICE_ID = "service_od5it5v";
const EMAILJS_TEMPLATE_ID = "template_x72y7ro";
const EMAILJS_PUBLIC_KEY = "izvYj0Wks1SjFJkM4";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("inicio");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Detect active section
      const sections = ["inicio", "nosotros", "valores", "servicios", "metodologia", "contacto"];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#inicio", label: "Inicio", id: "inicio" },
    { href: "#nosotros", label: "Nosotros", id: "nosotros" },
    { href: "#servicios", label: "Servicios", id: "servicios" },
    { href: "#valores", label: "Valores", id: "valores" },
    { href: "#metodologia", label: "Metodología", id: "metodologia" },
    { href: "#contacto", label: "Contacto", id: "contacto" }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? "py-2" 
          : "py-4"
      }`}
      data-testid="navbar"
    >
      <div className={`max-w-7xl mx-auto px-4 md:px-8 transition-all duration-500 ${
        isScrolled ? "py-0" : "py-2"
      }`}>
        <div className={`flex items-center justify-between rounded-full px-6 py-3 transition-all duration-500 ${
          isScrolled 
            ? "bg-[#1A1A1A]/90 backdrop-blur-xl shadow-lg shadow-black/20 border border-white/10" 
            : "bg-transparent"
        }`}>
          {/* Logo */}
          <a href="#inicio" data-testid="logo-link" className="flex items-center gap-3 group">
            <img src={LOGO} alt="Globex Ventures" className="h-9 md:h-10 transition-transform duration-300 group-hover:scale-105 brightness-0 invert" />
          </a>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center">
            <div className="flex items-center gap-1 bg-white/5 rounded-full p-1.5 backdrop-blur-sm">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                    activeSection === link.id
                      ? "text-white bg-[#01ABAE] shadow-lg shadow-[#01ABAE]/30"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white transition-all duration-300 hover:bg-white/20"
            data-testid="mobile-menu-btn"
          >
            <motion.div
              animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </motion.div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden absolute top-full left-4 right-4 mt-2 bg-[#1A1A1A]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-4 gap-1">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                    activeSection === link.id
                      ? "text-white bg-[#01ABAE]"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {link.label}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      ref={ref}
      id="inicio"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax Background */}
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center"
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="space-y-8"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 rounded-full glass text-[#01ABAE] text-sm font-medium tracking-wide"
          >
            LOGISTICS & TRACKING SERVICES
          </motion.span>

          <motion.h1
            variants={fadeInUp}
            className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-tight"
          >
            Moviendo el Motor del{" "}
            <span className="gradient-text">Comercio</span>
            <br />
            en México y Más Allá
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed"
          >
            Soluciones de ingeniería logística de alta precisión para tu carga pesada y sobredimensionada. 
            Tecnología de punta y personal experto a tu servicio.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center pt-4"
          >
            <a
              href="#contacto"
              className="btn-primary rounded-sm animate-pulse-glow inline-flex items-center gap-2 justify-center"
              data-testid="hero-cta-primary"
            >
              Solicita una Cotización
              <ArrowRight size={18} />
            </a>
            <a
              href="#servicios"
              className="btn-secondary rounded-sm inline-flex items-center gap-2 justify-center"
              data-testid="hero-cta-secondary"
            >
              Conoce Nuestros Servicios
              <ChevronDown size={18} />
            </a>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ChevronDown size={32} className="text-white/50" />
      </motion.div>
    </section>
  );
};

// About Section
const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section
      ref={ref}
      id="nosotros"
      className="py-24 md:py-32 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="order-2 lg:order-1"
          >
            <motion.span
              variants={fadeInUp}
              className="text-[#01ABAE] font-medium tracking-wide text-sm"
            >
              NUESTRA EMPRESA
            </motion.span>

            <motion.h2
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#262626] mt-4"
            >
              Especialistas en{" "}
              <span className="text-[#F4861C]">Logística Integral</span>
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-6 mt-8">
              <p className="text-lg text-[#52525B] leading-relaxed">
                En <strong className="text-[#262626]">Globex Ventures</strong>, no solo transportamos carga; movemos el motor del comercio en México con soluciones de alta ingeniería logística.
              </p>
              <p className="text-lg text-[#52525B] leading-relaxed">
                Somos especialistas en el manejo de carga pesada y sobredimensionada, operando con una flota de vanguardia que garantiza la integridad de cada envío.
              </p>
              <p className="text-lg text-[#52525B] leading-relaxed">
                Nuestra presencia local y foránea nos convierte en el aliado estratégico para industrias que no pueden permitirse retrasos. Con nosotros, su mercancía viaja con el respaldo de expertos que dominan cada kilómetro del trayecto.
              </p>
            </motion.div>

          </motion.div>

          {/* Image */}
          <motion.div
            style={{ y: imageY }}
            className="order-1 lg:order-2 relative"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-lg overflow-hidden shadow-2xl"
            >
              <img
                src={ABOUT_IMG}
                alt="Globex Ventures Global Logistics"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#262626]/50 to-transparent" />
            </motion.div>
            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="absolute -bottom-6 -left-6 bg-[#01ABAE] text-white p-6 rounded-lg shadow-xl"
            >
              <div className="text-3xl font-semibold">2030</div>
              <div className="text-sm opacity-90">Visión Global</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Misión y Visión - Debajo del contenido principal */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8 mt-20"
        >
          <motion.div variants={fadeInUp} className="p-8 bg-[#F8F9FA] rounded-lg border-l-4 border-[#01ABAE]">
            <h3 className="text-2xl font-semibold text-[#262626] mb-4 flex items-center gap-3">
              <Target size={28} className="text-[#01ABAE]" />
              Misión
            </h3>
            <p className="text-[#52525B] leading-relaxed text-lg">
              Proveer soluciones de autotransporte especializado y logística integral de clase mundial, garantizando entregas puntuales y seguras que impulsen la competitividad de nuestros clientes mediante tecnología de punta y personal altamente capacitado.
            </p>
          </motion.div>
          <motion.div variants={fadeInUp} className="p-8 bg-[#F8F9FA] rounded-lg border-l-4 border-[#F4861C]">
            <h3 className="text-2xl font-semibold text-[#262626] mb-4 flex items-center gap-3">
              <Cpu size={28} className="text-[#F4861C]" />
              Visión
            </h3>
            <p className="text-[#52525B] leading-relaxed text-lg">
              Consolidarnos para el 2030 como la corporación logística más confiable y eficiente a nivel internacional, siendo referentes en el transporte de carga pesada y liderando la transición hacia una logística inteligente, sostenible y sin fronteras.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      icon: <Truck size={32} />,
      title: "Autotransporte Local y Foráneo",
      description: "Movimiento de carga pesada y sobredimensionada con equipo certificado para trayectos urbanos y cruces interestatales de alta exigencia.",
      featured: false
    },
    {
      icon: <Package size={32} />,
      title: "Logística de Transporte",
      description: "Gestión inteligente de la cadena de suministro, desde la recolección en planta hasta la entrega final con trazabilidad total.",
      featured: false
    },
    {
      icon: <Target size={32} />,
      title: "Maniobras de Carga",
      description: "Servicios especializados de grúas y personal experto para el manejo de maquinaria industrial con seguridad total.",
      featured: false
    },
    {
      icon: <Warehouse size={32} />,
      title: "Almacenamiento Cross-Docking",
      description: "Centros de consolidación estratégicos que agilizan el flujo de mercancías, reduciendo tiempos y costos de inventario.",
      featured: false
    },
    {
      icon: <FileCheck size={32} />,
      title: "Gestión de Trámites y Custodia",
      description: "Asesoría en permisos de exceso de dimensiones y servicios de escolta y monitoreo satelital 24/7 para mercancías críticas.",
      featured: false
    }
  ];

  return (
    <section
      id="servicios"
      className="py-24 md:py-32 bg-[#1A1A1A]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="text-[#01ABAE] font-medium tracking-wide text-sm"
          >
            NUESTROS SERVICIOS
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-white mt-4"
          >
            Soluciones <span className="text-[#F4861C]">Integrales</span>
          </motion.h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Service Cards */}
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              className="card-hover bg-[#262626] border border-white/10 rounded-lg p-8 group"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 rounded-lg bg-[#01ABAE]/10 flex items-center justify-center text-[#01ABAE] mb-6 group-hover:bg-[#01ABAE] group-hover:text-white transition-all duration-300">
                {service.icon}
              </div>
              <h3 className="text-xl font-medium text-[#F4861C] mb-3">{service.title}</h3>
              <p className="text-white leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Values Section
const Values = () => {
  const values = [
    {
      number: "01",
      title: "Seguridad Absoluta",
      description: "Priorizamos la integridad de la carga, el personal y el entorno mediante protocolos de prevención y monitoreo constante.",
      icon: <Shield size={24} />
    },
    {
      number: "02",
      title: "Precisión Logística",
      description: "La puntualidad es nuestra norma; cada ruta es planeada milimétricamente para optimizar tiempos y recursos.",
      icon: <Target size={24} />
    },
    {
      number: "03",
      title: "Innovación en Movimiento",
      description: "Invertimos constantemente en tecnología de rastreo y mantenimiento preventivo para asegurar una flota siempre lista.",
      icon: <Cpu size={24} />
    },
    {
      number: "04",
      title: "Resiliencia Operativa",
      description: "Capacidad de respuesta inmediata ante cualquier desafío en ruta, garantizando que el flujo nunca se detenga.",
      icon: <Clock size={24} />
    }
  ];

  return (
    <section
      id="valores"
      className="py-24 md:py-32 bg-[#F8F9FA]"
      data-testid="values-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="text-[#01ABAE] font-medium tracking-wide text-sm"
          >
            DISTINTIVOS GLOBEX
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#262626] mt-4"
          >
            Nuestros <span className="text-[#F4861C]">Valores</span>
          </motion.h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={value.number}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              className="group flex gap-6 p-8 bg-white rounded-lg border border-black/5 hover:border-[#01ABAE]/30 transition-all duration-300 hover:shadow-lg"
              data-testid={`value-card-${index}`}
            >
              <div className="flex-shrink-0">
                <span className="text-6xl font-semibold text-[#01ABAE]/20 group-hover:text-[#01ABAE]/40 transition-colors">
                  {value.number}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[#F4861C]">{value.icon}</span>
                  <h3 className="text-xl font-medium text-[#262626]">{value.title}</h3>
                </div>
                <p className="text-[#52525B] leading-relaxed">{value.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Methodology Section
const Methodology = () => {
  const steps = [
    {
      step: "01",
      title: "Ingeniería de Ruta Predictiva",
      subtitle: "IA",
      description: "No solo trazamos mapas; utilizamos algoritmos de Inteligencia Artificial para analizar el clima, tráfico y condiciones viales en tiempo real. Esto nos permite anticipar contingencias y ajustar trayectos antes de que el motor se ponga en marcha, garantizando puntualidad matemática.",
      image: "https://images.unsplash.com/photo-1643199350552-2d970ecad7e3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400"
    },
    {
      step: "02",
      title: "Certificación de Flota Elite",
      subtitle: "VANGUARDIA",
      description: "Operamos exclusivamente con equipo propio de última generación, sometido a un programa de mantenimiento preventivo riguroso. Cada unidad cuenta con certificaciones internacionales de seguridad, lo que elimina el riesgo de fallas mecánicas y asegura una disponibilidad inmediata para cualquier tonelaje.",
      image: "https://images.unsplash.com/photo-1682033239487-30f076c82232?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400"
    },
    {
      step: "03",
      title: "Operadores con Certificación Elite",
      subtitle: "EXPERTOS",
      description: "Nuestros conductores no solo manejan; son especialistas certificados en el transporte de carga sobredimensionada y materiales críticos. Reciben capacitación continua en maniobras de alta precisión y protocolos de seguridad, asegurando una pericia inigualable al volante de su inversión.",
      image: "https://images.unsplash.com/photo-1574757974346-45bae947d89a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400"
    },
    {
      step: "04",
      title: "Monitoreo Total y Custodia Activa",
      subtitle: "24/7",
      description: "Implementamos una torre de control 24/7 con trazabilidad absoluta mediante GPS y sensores de telemetría. Para mercancía crítica, integramos protocolos de custodia preventiva y escolta, garantizando que el cliente sepa exactamente dónde está su carga en cada segundo del viaje.",
      image: "https://images.unsplash.com/photo-1722118177362-7c1203f05e3d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400"
    },
    {
      step: "05",
      title: "Maniobras Especializadas de Ingeniería",
      subtitle: "PRECISIÓN",
      description: "Planificamos técnicamente cada proceso de carga y descarga utilizando equipo hidráulico y grúas de alta capacidad. Nuestros ingenieros de campo supervisan personalmente el trincado y aseguramiento de la carga, aplicando leyes de física y estiba para que el producto viaje sin el mínimo movimiento.",
      image: "https://images.unsplash.com/photo-1775049065011-35145919f2d5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400"
    },
    {
      step: "06",
      title: "Consolidación y Entregas Inteligentes",
      subtitle: "CERO FRICCIONES",
      description: "Gestionamos el flujo de stock en centros estratégicos de cross-docking para agilizar la distribución final. Esta fase optimiza la logística de productos, reduciendo tiempos de estancia y asegurando una entrega \"última milla\" que maximiza la rentabilidad de su cadena de suministro.",
      image: "https://images.unsplash.com/photo-1571989928541-674d0cf46c4a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400"
    }
  ];

  return (
    <section
      id="metodologia"
      className="py-24 md:py-32 bg-white"
      data-testid="methodology-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="text-[#01ABAE] font-medium tracking-wide text-sm"
          >
            NUESTRA METODOLOGÍA
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#262626] mt-4"
          >
            GLOBEX <span className="text-[#F4861C]">TRANS-PRECISION</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg text-[#52525B] max-w-3xl mx-auto mt-6"
          >
            Un sistema integral que garantiza la excelencia en cada etapa de tu operación logística
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.step}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              className="relative bg-[#F8F9FA] rounded-lg overflow-hidden border border-[#01ABAE]/10 hover:border-[#01ABAE]/30 transition-all group hover:shadow-lg"
              data-testid={`methodology-step-${index}`}
            >
              {/* Image */}
              <div className="h-48 overflow-hidden">
                <img 
                  src={step.image} 
                  alt={step.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-black/30 to-transparent" />
                <span className="absolute top-4 left-4 text-5xl font-semibold text-white/30">
                  {step.step}
                </span>
                <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#F4861C] text-white text-xs font-medium">
                  {step.subtitle}
                </span>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-[#262626] mb-3">{step.title}</h3>
                <p className="text-[#52525B] text-sm leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const formRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    phone: "",
    message: ""
  });

  useEffect(() => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_name || !formData.user_email || !formData.phone || !formData.message) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.user_email)) {
      toast.error("Por favor ingresa un correo electrónico válido");
      return;
    }

    setIsLoading(true);

    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );

      toast.success("¡Mensaje enviado exitosamente! Nos pondremos en contacto pronto.");
      setFormData({ user_name: "", user_email: "", phone: "", message: "" });
    } catch (error) {
      console.error("EmailJS Error:", error);
      toast.error("Hubo un error al enviar el mensaje. Por favor intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      className="py-24 md:py-32 bg-[#1A1A1A]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.span
              variants={fadeInUp}
              className="text-[#01ABAE] font-medium tracking-wide text-sm"
            >
              CONTÁCTANOS
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-white mt-4"
            >
              ¿Listo para <span className="text-[#F4861C]">Optimizar</span> tu Logística?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#A1A1AA] mt-6"
            >
              Nuestro equipo de expertos está listo para diseñar la solución perfecta para tus necesidades de transporte y logística.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-6 mt-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#01ABAE]/10 flex items-center justify-center text-[#01ABAE] flex-shrink-0">
                  <MapPin size={24} />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Dirección</h4>
                  <p className="text-[#A1A1AA]">
                    Calle San Juan de Letrán No.4341 Int. 7,<br />
                    Residencial Cordilleras, C.P. 45039,<br />
                    Zapopan, Jalisco, México
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#F4861C]/10 flex items-center justify-center text-[#F4861C] flex-shrink-0">
                  <Phone size={24} />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Teléfono</h4>
                  <a href="tel:+523339559588" className="text-[#A1A1AA] hover:text-[#01ABAE] transition-colors">
                    +52 33 3955 9588
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#01ABAE]/10 flex items-center justify-center text-[#01ABAE] flex-shrink-0">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Correo Electrónico</h4>
                  <a href="mailto:contacto@globexventures.com" className="text-[#A1A1AA] hover:text-[#01ABAE] transition-colors">
                    contacto@globexventures.com
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-[#262626] rounded-lg p-8 md:p-10 border border-white/10"
          >
            <h3 className="text-2xl font-medium text-white mb-8">Envíanos un mensaje</h3>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="user_name" className="block text-sm text-[#A1A1AA] mb-2">
                  Nombre completo
                </label>
                <input
                  type="text"
                  id="user_name"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleChange}
                  className="input-custom w-full rounded-sm"
                  placeholder="Tu nombre"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="user_email" className="block text-sm text-[#A1A1AA] mb-2">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  id="user_email"
                  name="user_email"
                  value={formData.user_email}
                  onChange={handleChange}
                  className="input-custom w-full rounded-sm"
                  placeholder="tu@email.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm text-[#A1A1AA] mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-custom w-full rounded-sm"
                  placeholder="+52 33 1234 5678"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm text-[#A1A1AA] mb-2">
                  Mensaje
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="input-custom w-full rounded-sm resize-none"
                  placeholder="Cuéntanos sobre tu proyecto de logística..."
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Enviar Mensaje
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white border-t border-black/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <img src={LOGO} alt="Globex Ventures" className="h-12 mb-6" />
            <p className="text-[#262626]/80 leading-relaxed max-w-md">
              Movemos el motor del comercio en México con soluciones de alta ingeniería logística. 
              Especialistas en carga pesada y sobredimensionada.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#262626] font-medium mb-6">Enlaces Rápidos</h4>
            <nav className="space-y-3">
              <a href="#inicio" className="block text-[#262626]/70 hover:text-[#262626] transition-colors">Inicio</a>
              <a href="#nosotros" className="block text-[#262626]/70 hover:text-[#262626] transition-colors">Nosotros</a>
              <a href="#servicios" className="block text-[#262626]/70 hover:text-[#262626] transition-colors">Servicios</a>
              <a href="#contacto" className="block text-[#262626]/70 hover:text-[#262626] transition-colors">Contacto</a>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[#262626] font-medium mb-6">Contacto</h4>
            <div className="space-y-3 text-[#262626]/70">
              <p>Zapopan, Jalisco, México</p>
              <a href="tel:+523339559588" className="block hover:text-[#262626] transition-colors">
                +52 33 3955 9588
              </a>
              <a href="mailto:contacto@globexventures.com" className="block hover:text-[#262626] transition-colors">
                contacto@globexventures.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#262626]/20 mt-12 pt-8 flex flex-col md:flex-row items-center justify-center gap-4">
          <p className="text-[#262626]/70 text-sm">
            © {currentYear} Globex Ventures. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component with Lenis
function App() {
  useEffect(() => {
    // Initialize Lenis smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#262626',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      />
      <Navbar />
      <main>
        <Hero />
        <About />
        <Values />
        <Services />
        <Methodology />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
